import { memo, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import LottiePlayer from './LottiePlayer'

const INTRO_MIN_MS = 800
const INTRO_FADE_MS = 500
// Public Lottie files are not fingerprinted by Vite. Bump the query whenever
// the artwork is replaced so Telegram WebView cannot reuse an older cached
// animation from the same URL.
const INTRO_ANIMATION_URL = '/images/lottie/json/load.json?v=20260907-0803'

function IntroAnimation() {
  const [canPlayLottie, setCanPlayLottie] = useState(null)

  useEffect(() => {
    let cancelled = false

    fetch(INTRO_ANIMATION_URL)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`)
        }
        return response.json()
      })
      .then(() => {
        if (!cancelled) {
          setCanPlayLottie(true)
        }
      })
      .catch((error) => {
        console.warn('[IntroScreen] intro animation unavailable:', error)
        if (!cancelled) {
          setCanPlayLottie(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  if (canPlayLottie === true) {
    return <LottiePlayer animationUrl={INTRO_ANIMATION_URL} className="h-40 w-40" />
  }

  if (canPlayLottie === false) {
    return (
      <div className="intro-screen__fallback flex h-40 w-40 items-center justify-center rounded-full">
        <span className="text-3xl font-black" aria-hidden="true">
          ★
        </span>
      </div>
    )
  }

  return <div className="intro-screen__skeleton h-40 w-40 animate-pulse rounded-full" aria-hidden="true" />
}

function IntroScreen({ isReady, onComplete }) {
  const [isVisible, setIsVisible] = useState(true)
  const [isFading, setIsFading] = useState(false)
  const mountedAtRef = useRef(Date.now())
  const completionStartedRef = useRef(false)

  useEffect(() => {
    if (!isReady || completionStartedRef.current) {
      return undefined
    }

    const elapsed = Date.now() - mountedAtRef.current
    const waitMs = Math.max(0, INTRO_MIN_MS - elapsed)
    let fadeTimer

    const startTimer = window.setTimeout(() => {
      completionStartedRef.current = true
      setIsFading(true)

      fadeTimer = window.setTimeout(() => {
        setIsVisible(false)
        onComplete?.()
      }, INTRO_FADE_MS)
    }, waitMs)

    return () => {
      window.clearTimeout(startTimer)
      if (fadeTimer) {
        window.clearTimeout(fadeTimer)
      }
    }
  }, [isReady, onComplete])

  if (!isVisible || typeof document === 'undefined') {
    return null
  }

  return createPortal(
    <div
      className={`public-app intro-screen fixed inset-0 z-[99999] flex flex-col items-center justify-center transition-opacity duration-500 ${
        isFading ? 'opacity-0' : 'opacity-100'
      }`}
      role="status"
      aria-live="polite"
      aria-hidden={isFading}
    >
      <div className="intro-screen__content">
        <IntroAnimation />
        <h1 className="intro-screen__title">Patrick Stars</h1>
        <p className="intro-screen__tagline">Telegram STARS, Premium, NFTs</p>
        <div className="intro-screen__spinner" aria-hidden="true" />
      </div>
    </div>,
    document.body,
  )
}

export default memo(IntroScreen)
